import { Box, Button, Group, Tabs } from "@mantine/core"
import { FileTextIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DropShippingApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DropShippingMode, SupplierDropShipping, SupplierDropShippingRef } from "../../components/supplier-dropshipping/list"
import { ReasonTooltip, SegmentedTabs } from "../../components/ui"
import { useHelpContext } from "@dehub/theme"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"

/** Tab -> list mode. Each tab has its own permission (BE order/dropshipping/list.ts accepts either). */
const TAB_MODE: Record<string, DropShippingMode> = { SOLD_BY_PARTNER: "partner", SOLD_BY_ME: "mine" }

export function SupplierDropShippingTabs() {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_DRSHPING = usePermission("ORD_DRSHPING")
  const ORD_DROPPED = usePermission("ORD_DROPPED")
  const [activeTab, setActiveTab] = useState<string>(ORD_DRSHPING.hasAccess || !ORD_DROPPED.hasAccess ? "SOLD_BY_PARTNER" : "SOLD_BY_ME")
  const [excelBusy, setExcelBusy] = useState(false)
  const listRef = useRef<SupplierDropShippingRef>(null)

  const activePerm = activeTab === "SOLD_BY_PARTNER" ? ORD_DRSHPING : ORD_DROPPED

  const onExcel = async () => {
    setExcelBusy(true)
    try {
      const { url } = await DropShippingApi.excel({ filter: listRef.current?.getFilter(), order: listRef.current?.getOrder() })
      window.open(url, "_blank")
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setExcelBusy(false)
    }
  }

  // @dehub/help: the tab is the page content variant (`when`): source supplier vs dropper perspective.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => { setHelpContext({ when: activeTab }) }, [activeTab, setHelpContext])

  return (
    <PageLayout
      title={t("supplierSales:dropShipping.title")}
      subTitle={t("supplierSales:dropShipping.subtitle")}
      breadcrumb={[{ to: "/", label: t("common:breadcrumb.home") }, { label: t("supplierSales:dropShipping.breadcrumb") }]}
      extra={[
        <ReasonTooltip key="excel" reason={activePerm.reason}>
          <Button variant="default" leftSection={<FileTextIcon size={16} />} loading={excelBusy} onClick={onExcel}>
            {t("supplierSales:dropShipping.fileLabel")}
          </Button>
        </ReasonTooltip>,
      ]}>
      {/* Segmented tabs: one list-header concept with Борлуулалт / Худалдан авалт / network. A tab without its permission opens the «Эрх хэрэгтэй» card. */}
      {/* The page layout's 20px gap is the only space between the tabs and the list (no extra margins). */}
      <Group>
        <SegmentedTabs value={activeTab} onChange={setActiveTab} data={[{ value: "SOLD_BY_PARTNER", label: t("supplierSales:dropShipping.tabFromUs") }, { value: "SOLD_BY_ME", label: t("supplierSales:dropShipping.tabSupplier") }]} />
      </Group>
      <Tabs keepMounted={false} value={activeTab}>
        {Object.entries(TAB_MODE).map(([value, mode]) => {
          const perm = value === "SOLD_BY_PARTNER" ? ORD_DRSHPING : ORD_DROPPED
          return (
            <Tabs.Panel key={value} value={value}>
              {perm.hasAccess ? <SupplierDropShipping ref={listRef} mode={mode} /> : <Box>{perm.accessDenied()}</Box>}
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </PageLayout>
  )
}
